import httpClient from '../httpClient';
import { AxiosRequestConfig } from 'axios';
import { BossModeSkipPayment, Listing, TaxInvoice } from 'models/Listing';
import { EnquiryFormValues } from 'router/subrouters/Dash/subrouters/Business/components/EnquirySlider/utils';
import { DropzoneFile } from 'components/Dropzone/utils';
import { PaginatedData, PaginationParams } from 'models/PaginatedData';
import {
  getUploadedFile,
  getFileParams,
  uploadFileToUrl,
} from 'uploadFileUtils';
import { DeepPartial } from 'types';
import { PresignedFileResponse, UploadedFileResponse } from 'models/File';
import { DealRoom } from 'models/DealRoom';

function getListing(listingId: string, params?: PaginationParams) {
  return httpClient.get<Record<'listing', Listing>>(`listings/${listingId}`, {
    params,
  });
}

function getListingWithSlug(slug: string, params?: PaginationParams) {
  return httpClient.get<Record<'listing', Listing>>(
    `listings/slug-names/${slug}`,
    {
      params,
    },
  );
}

async function uploadFile(
  file: File,
  fileName: string,
  listingId: string,
  config: AxiosRequestConfig,
  customName?: string,
) {
  const fileParams = getFileParams(fileName, file, customName);

  const { data } = await httpClient.post<
    UploadedFileResponse & { listing: Listing }
  >(`listings/${listingId}/upload-temporary-object`, fileParams);

  const { listing, ...rest } = data;

  const uploadedFile = await getUploadedFile(rest, file, config);

  return { listing, uploadedFile };
}

function deleteFile(listingId: string, fileName: string, fileId: string) {
  return httpClient.delete<{ fileId: string; listing: Listing }>(
    `listings/${listingId}/files/${fileName}/${fileId}`,
  );
}

function patchListing(listingId: string, listing: DeepPartial<Listing>) {
  return httpClient.patch<{ listing: Listing }>(
    `listings/${listingId}`,
    listing,
  );
}

function getListings(params?: Record<string, any>) {
  return httpClient.get<PaginatedData<Listing>>(`listings`, {
    params,
  });
}

function getDistressedListings(params?: Record<string, any>) {
  return httpClient.get<PaginatedData<Listing>>(`listings/distressed`, {
    params,
  });
}

function getSimilarListings(listingId: string, params?: Record<string, any>) {
  return httpClient.get<Array<Listing>>(
    `listings/${listingId}/similar-businesses`,
    {
      params,
    },
  );
}

function updateListingByStep(
  listingId: string,
  currentStep: string,
  listingValues: Partial<Listing>,
) {
  return httpClient.put<{ listing: Partial<Listing> }>(
    `listings/${listingId}/${currentStep}`,
    listingValues,
  );
}

function addWatchlist(listingId: string) {
  return httpClient.post(`listings/${listingId}/add-watchlist`);
}

function removeWatchlist(listingId: string) {
  return httpClient.post(`listings/${listingId}/remove-watchlist`);
}

function createPaymentIntent(listingId: string) {
  return httpClient.post(`listings/${listingId}/create-payment-intent`);
}

function publishListing(
  listingId: string,
  payWithInvoice: boolean,
  taxInvoice?: TaxInvoice,
) {
  return httpClient.post(`listings/${listingId}`, {
    taxInvoice,
    payWithInvoice,
  });
}

function checkABN(listingId: string, ABN: string) {
  return httpClient.post<{ exist: boolean }>(
    `listings/${listingId}/check-exist-ABN`,
    {
      ABN,
    },
  );
}

function checkACN(listingId: string, ACN: string) {
  return httpClient.post<{ exist: boolean }>(
    `listings/${listingId}/check-exist-ACN`,
    {
      ACN,
    },
  );
}

function registerInterest(listingId: string, buyerInfo: EnquiryFormValues) {
  return httpClient.post<{ ndaUrl: string }>(
    `listings/${listingId}/register-interest`,
    buyerInfo,
  );
}

function deleteListingContact(
  fileName: string,
  fileId: string,
  listingId: string,
) {
  return httpClient.delete<{ fileId: string; listing: Listing }>(
    `listings/${listingId}/files-listing-contact/${fileName}/${fileId}`,
  );
}

function publishWithoutPayment(listingId: string, data: BossModeSkipPayment) {
  return httpClient.post(`listings/${listingId}/publish-without-payment`, data);
}

function doesSlugExist(slugName: string, listingId: string) {
  return httpClient.post<{ exist: boolean; slugName?: string }>(
    `listings/${listingId}/check-exist-slug`,
    {
      slugName,
    },
  );
}

function declineInterest(listingId: string) {
  return httpClient.post(`listings/${listingId}/decline-interest`);
}

function reorderGallery(listingId: string, fileIds: string[]) {
  return httpClient.post(`listings/${listingId}/reorder-gallery`, { fileIds });
}

function setPrivateListing(listingId: string) {
  return httpClient.post(`listings/${listingId}/set-private-listing`);
}

function getLatestListings(params?: PaginationParams) {
  return httpClient.get<PaginatedData<Listing>>('listings/latest', {
    params,
  });
}

function viewTeaser(listingId: string) {
  return httpClient.post(`listings/${listingId}/view-teaser`);
}

function getTeaserVideo(listingId: string) {
  return httpClient.get<DropzoneFile>(
    `listings/${listingId}/video-teaser-file`,
  );
}

async function uploadListingFileToAws(
  listingId: string,
  file: File,
  name: string,
  config: AxiosRequestConfig,
) {
  const fileParams = getFileParams(name, file);

  const { data } = await httpClient.post<PresignedFileResponse>(
    `listings/${listingId}/upload-temporary-object`,
    fileParams,
  );

  await uploadFileToUrl(data.presignedPost, file, config);
}

function extendCampaign(listingId: string, extensionDays: number) {
  return httpClient.post<DealRoom>(`listings/${listingId}/extend-campaign`, {
    extensionDays,
  });
}

export default {
  doesSlugExist,
  publishWithoutPayment,
  checkACN,
  checkABN,
  publishListing,
  createPaymentIntent,
  getListings,
  uploadFile,
  deleteFile,
  patchListing,
  getListing,
  updateListingByStep,
  addWatchlist,
  removeWatchlist,
  registerInterest,
  getSimilarListings,
  getDistressedListings,
  getListingWithSlug,
  declineInterest,
  reorderGallery,
  setPrivateListing,
  getLatestListings,
  viewTeaser,
  uploadListingFileToAws,
  getTeaserVideo,
  deleteListingContact,
  extendCampaign,
};
